import { useState } from "react";
import { Link } from "react-router-dom";

import {
  AUDIT_DOMAINS,
  actorEmail,
  auditStamp,
  auditTarget,
  auditValue,
  eventsOf,
  type AuditDomain,
} from "../../api/mcp";
import { CouldNotLook, Denied, LoadFailed, Loading, SignedOut } from "../../components/states";
import { Badge, Empty, FilterBar, Notice, Page, SelectField, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { useAuditFeed } from "../../hooks/useAuditFeed";
import { ProductScope } from "../../products/PerProduct";
import { Button } from "../../f7";

/** Where a target can be opened, when the console has a page for it. */
function targetLink(e: { domain: AuditDomain; target: unknown }): string | null {
  // A row with no target — or one carrying a non-string, which the server's
  // deserializer can emit — names nothing to open. It is not linkable, and it
  // is not a crash either; see the note on AuditEvent.target.
  const target = auditTarget(e.target);
  if (!target) return null;
  if (e.domain === "tenant") return `/tenants/${encodeURIComponent(target)}`;
  // A quota target is "<tier>" or "<tier>/<tool>"; the tier page covers both.
  if (e.domain === "quota") return `/policy/caps/${encodeURIComponent(target.split("/")[0] ?? "")}`;
  return null;
}

/**
 * Who changed what, through this console.
 *
 * NOT merchant tool-call audit. Different table, different question, and the
 * API reference says explicitly never to merge the two into one panel — an
 * operator asking "who capped this tool" and one asking "what did this
 * merchant's assistant do" are looking for opposite things.
 *
 * This is also the ONLY durable record of two events. A blocklist row is
 * deleted on unblock, so nothing else remembers that someone was ever blocked
 * or who lifted it; a cap's `updated_by` stamp is overwritten on every change,
 * so nothing else remembers who set the previous value.
 */
export function Audit({ config }: { config: AppConfig }) {
  const [domain, setDomain] = useState<AuditDomain | "">("");
  const { state, reload, loadMore, loadingMore, moreError } = useAuditFeed(config, domain);

  return (
    <Page
      title="Audit log"
      subtitle="Admin changes made through this console — not merchant tool calls."
    >

      {/* WHOSE LOG. Each product's admin API keeps its own audit table, and
          this reads MCP's. An operator investigating "who changed this
          merchant's tier" gets a complete answer for MCP and a silent, empty
          one for Labeeb — and an audit log that looks complete while omitting a
          product is worse than one that admits the gap. */}
      <ProductScope owns={["mcp"]}>
        each product keeps its own audit table — a change made in Labeeb would not appear here
      </ProductScope>

      {/* One filter row, not a flex row wrapping another flex row. FilterBar
          already lays its children out on one line with the design's gap. */}
      <FilterBar>
        <SelectField
          value={domain}
          onChange={(v) => setDomain(v as AuditDomain | "")}
          label="Filter by domain"
          width={220}
        >
          <option value="">Everything, last 7 days</option>
          {AUDIT_DOMAINS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </SelectField>
        {!domain && (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            The unfiltered view is windowed to 7 days; a domain filter is not.
          </span>
        )}
      </FilterBar>

      {state.kind === "loading" && <Loading what="the audit log" />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "unavailable" && (
        <CouldNotLook what="the audit log" message={state.error.message} onRetry={reload} />
      )}
      {/* A 422 means the domain itself was rejected — "you asked for the wrong
          thing", never "nothing happened". Rendering it as an empty feed would
          report silence where there was a refusal. */}
      {state.kind === "invalid" && (
        <Notice tone="amber" title="That domain does not exist">
          {state.error.message} Valid domains are {AUDIT_DOMAINS.join(", ")}.
        </Notice>
      )}
      {state.kind === "notfound" && (
        <LoadFailed what="the audit log" message="Route not found on this gateway." />
      )}
      {state.kind === "error" && (
        <LoadFailed what="the audit log" message={state.error.message} />
      )}

      {/* `Load more` lives OUTSIDE the has-rows branch. A DynamoDB page can
          come back empty while later pages still hold rows — the cursor, not
          the row count, is what says whether there is more — and gating the
          button on `events.length > 0` stranded the feed on "No admin changes"
          with unread pages behind it. */}
      {state.kind === "ready" && (
        <>
          {eventsOf(state.data).length === 0 ? (
            <Empty>
              {domain ? `No ${domain} changes recorded.` : "No admin changes in the last 7 days."}
            </Empty>
          ) : (
          <Table
            head={
              <tr>
                <Th>When</Th>
                <Th>Who</Th>
                <Th>Action</Th>
                <Th>Target</Th>
                <Th>Change</Th>
              </tr>
            }
          >
            {eventsOf(state.data).map((e, i) => {
              const to = targetLink(e);
              const target = auditTarget(e.target);
              return (
                <tr key={`${e.timestamp}-${i}`}>
                  <Td>
                    <span
                      className="text-xs text-slate-500 dark:text-slate-400"
                      title={e.timestamp ?? undefined}
                    >
                      {auditStamp(e.timestamp)}
                    </span>
                  </Td>
                  <Td>{actorEmail(e.actor)}</Td>
                  <Td>
                    <Badge>{e.action}</Badge>
                    <span className="ml-1.5 text-xs text-slate-400 dark:text-slate-500">{e.domain}</span>
                  </Td>
                  <Td>
                    {to ? (
                      <Link to={to} className="ref">
                        {target}
                      </Link>
                    ) : target ? (
                      <span className="ref">{target}</span>
                    ) : (
                      // An empty cell would read as "changed nothing"; this row
                      // did change something and did not record what.
                      <span className="text-slate-400 dark:text-slate-500">not recorded</span>
                    )}
                  </Td>
                  <Td>
                    <span className="text-xs">
                      <span className="text-slate-500 dark:text-slate-400">{auditValue(e.before)}</span>
                      <span className="mx-1 text-slate-400 dark:text-slate-500">→</span>
                      <span>{auditValue(e.after)}</span>
                    </span>
                  </Td>
                </tr>
              );
            })}
          </Table>
          )}

          {moreError && (
            <p className="mt-4 text-xs text-amber-700 dark:text-amber-400">{moreError}</p>
          )}
          {state.data.cursor && (
            <div className="mt-4">
              <Button outline disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}
    </Page>
  );
}
